import { useMemo } from 'react';

import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { UploadSessionStatus } from '@/shared/api/protocol-ts/model/dto_upload_session_pb';
import { useAppSelector } from '@/shared/hooks';

import { composedModel } from '@/entities/composed';
import { studyModel } from '@/entities/study';

import {
  reportCardsModel,
  type StructuredReportsAndStudiesType,
} from '@/features/reportCards';

import { type UnifiedItem } from '../config';
import { getUnifiedItems } from '../lib';

const NO_HIDDEN_REPORTS: StructuredReportsAndStudiesType[] = [];

export const usePatientProfileItems = (patientID: string) => {
  const canViewArchive = useAppSelector(
    composedModel.selectors.selectCanViewPatientReportsArchive(patientID),
  );

  const reportCardsLoading = useAppSelector(
    reportCardsModel.selectors.selectLoading,
  );

  const { sortedStructuredReportsAndStudies } = useAppSelector(
    reportCardsModel.selectors.selectStructuredReportsAndStudiesByPatientID(
      patientID,
    ),
  );

  // The hidden view is only built while the flag is on: with it off, archived
  // reports arriving through the live profile stream must not surface here.
  const hiddenStructuredReportsAndStudies = useAppSelector((state) =>
    canViewArchive
      ? reportCardsModel.selectors.selectHiddenStructuredReportsAndStudiesByPatientID(
          patientID,
        )(state).sortedStructuredReportsAndStudies
      : NO_HIDDEN_REPORTS,
  );

  const rawMeshViewerStudies = useAppSelector(
    studyModel.selectors.selectNotDeletedStudiesByPatientID(patientID),
  );

  const showSkeletons =
    reportCardsLoading === 'idle' || reportCardsLoading === 'pending';

  const rawMeshViewerItems = useMemo(
    () =>
      rawMeshViewerStudies
        .filter(
          (study) =>
            study.Type === StudyType.StudyType_IOS_Meshes &&
            study.RawMeshViewer &&
            study.Status === UploadSessionStatus.UploadSessionStatusSuccessful,
        )
        .map<UnifiedItem>((study) => ({
          type: 'rawMeshViewer',
          data: study,
          date: study.Created?.At?.toDate().getTime() ?? 0,
        })),
    [rawMeshViewerStudies],
  );

  const allItems = useMemo(
    () =>
      getUnifiedItems({
        reports: sortedStructuredReportsAndStudies,
        rawMeshViewerItems,
      }),
    [sortedStructuredReportsAndStudies, rawMeshViewerItems],
  );

  const hiddenItems = useMemo(
    () => getUnifiedItems({ reports: hiddenStructuredReportsAndStudies }),
    [hiddenStructuredReportsAndStudies],
  );

  return { allItems, hiddenItems, showSkeletons, canViewArchive };
};
